<script lang="ts" setup>
import { ref, watch } from 'vue';
import AccordionHeader from '@/components/common/accordions/AccordionHeader.vue';

const emit = defineEmits<{
    'change': [toggled: boolean];
    'close': [toggled: boolean];
    'open': [toggled: boolean];
}>();

const toggled = defineModel({
    type: Boolean,
    default: false
});

const props = defineProps({
    defaultState: {
        type: Boolean,
        default: false
    },
    title: {
        type: String,
        default: ''
    },
    horizontalPadding: {
        type: Boolean,
        default: true
    }
});

/**
 * Toggle the accordion
 */
function toggle(): void {
    toggled.value = !toggled.value;
    if (toggled.value) {
        emit('open', true);
    } else {
        emit('close', false);
    }
    emit('change', toggled.value);
}

function open(): void {
    toggled.value = true;
    emit('open', true);
}

function close(): void {
    toggled.value = false;
    emit('close', false);
}

const contentOpen = ref(props.defaultState);

watch(() => props.defaultState, () => {
    toggled.value = props.defaultState;
}, { immediate: true });

watch(() => toggled.value, () => {
    if (toggled.value) {
        setTimeout(() => {
            contentOpen.value = true;
        }, 400);
    } else {
        contentOpen.value = false;
    }
});
</script>

<template>
    <div class="sy-accordion-container">
        <slot
            name="trigger"
            :toggle="toggle"
            :toggled="toggled"
            :open="open"
            :close="close"
        >
            <AccordionHeader
                :class="['padding-vertical-5', horizontalPadding ? 'padding-horizontal-6' : 'padding-horizontal-0']"
                :title="title"
                :is-open="toggled"
                @click="toggled = !toggled"
            />
        </slot>
        <div :class="['sy-accordion-content', { 'open' : toggled}]">
            <div :class="['sy-content-inner', { 'open': contentOpen && toggled }]">
                <slot
                    :toggle="toggle"
                    :toggled="toggled"
                    :open="open"
                    :close="close"
                ></slot>
            </div>
        </div>
    </div>
</template>

<style lang="scss" scoped>
.sy-accordion-content {
    display: grid;
    transition: all 0.3s;
    grid-template-rows: 0fr;

    &.open {
        grid-template-rows: 1fr;
    }
    .sy-content-inner {
        overflow: hidden;
        &.open {
            overflow: visible;
        }
    }
}
</style>
